import {
  resolveRatingBounds,
  type RatingBoundsSpec,
} from '@/shared-question-model';

import { pickSingleSpokenValue } from './shared-responder-v2-core-option.helpers';
import {
  createThreadBehaviour,
  type ResponderDraftEvent,
} from './shared-responder-v2-core-thread-behaviour';
import { THREAD_KINDS } from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderAnswer,
} from './shared-responder-v2-core-thread.types';

export function clampRatingValue(
  value: number,
  spec: RatingBoundsSpec
): number {
  const { min, max } = resolveRatingBounds(spec);

  return Math.min(Math.max(value, min), max);
}

function resolveSpokenRatingValues(
  spokenValues: readonly string[]
): readonly number[] {
  return spokenValues
    .map((value) => value.trim())
    .filter((value) => value !== '')
    .map(Number)
    .filter(Number.isFinite);
}

export function reduceRatingDraft(
  draft: ResponderAnswer | null,
  event: ResponderDraftEvent,
  spec: QuestionSpec
): ResponderAnswer | null {
  if (event.type === 'spokenPick') {
    const spokenValues = resolveSpokenRatingValues(event.optionValues);

    if (spokenValues.length === 0) {
      return null;
    }

    const { min, max } = resolveRatingBounds(spec);
    const value = pickSingleSpokenValue(
      spokenValues.filter((spoken) => spoken >= min && spoken <= max)
    );

    return value === undefined ? draft : { kind: 'rating', value };
  }

  if (event.type !== 'ratingPicked') {
    return draft;
  }

  if (!Number.isFinite(event.value)) {
    return draft;
  }

  return { kind: 'rating', value: clampRatingValue(event.value, spec) };
}

export function isRatingDraftComplete(draft: ResponderAnswer | null): boolean {
  return draft?.kind === 'rating';
}

export const RATING_THREAD_BEHAVIOUR = createThreadBehaviour({
  kind: THREAD_KINDS.Rating,
  advancesOnSurfacePick: true,
  spokenAffordances: { extractsSpokenPick: true },
  emptyDraft: () => null,
  reduceDraft: reduceRatingDraft,
  isDraftComplete: isRatingDraftComplete,
});
